import {
  type TDeliveryData,
  type TDiscount,
  type TRecipeSelection,
  type TSubscriptionData,
  DiscountCodeType,
  type TDiscountCodeType,
} from "@./suf-store";

import { snakeToPascal } from "@utils/misc";

export const formatWholePrice = (price: number): string =>
  Number.isInteger(price) ? price.toString() : price.toFixed(2);

export const formatPrice = (price?: number | string, fallback = "£--"): string => {
  if (price === undefined || price === null) { return fallback; }
  const num = typeof price === "string" ? Number(price) : price;
  if (Number.isNaN(num)) { return fallback; }
  return `£${num.toFixed(2)}`;
};

/** Pass the translated day label (Shared.Time.day) so copy stays localisable. */
export const formatPricePerDay = (price?: number | string, dayLabel = "day"): string =>
  `${formatPrice(price)} / ${dayLabel}`;

export const formatDiscount = (
  discount: TDiscount | undefined,
): string => {
  if (!discount?.type || !discount?.value) { return ""; }

  const type = discount.type.toUpperCase() as TDiscountCodeType;

  switch (type) {
    case DiscountCodeType.PERCENTAGE:
      return `${discount.value}%`;
    case DiscountCodeType.FIXED_AMOUNT:
      return `£${discount.value}`;
    case DiscountCodeType.ABSOLUTE_TOTAL:
      return `£${discount.value}`;
    default: {

      const _exhaustive: never = type;
      console.warn(`Unknown discount type: ${_exhaustive}`);
      return "";
    }
  }
};

export const isAbsoluteDiscount = (discount: TDiscount | undefined): boolean =>
  discount?.type?.toUpperCase() === DiscountCodeType.ABSOLUTE_TOTAL;

/**
 * Total amount the customer saves on their trial box, shown in the order
 * summary and pricing summary. It sums everything they get vs paying full price:
 *
 *  - The trial-box discount (original minus discounted price). The trial-box
 *    totals already include the Nibbles Trio, so the nibbles half-price saving
 *    is part of this figure — we don't add nibbles separately.
 *  - The free sprinkles topper(s) they receive.
 *  - Free express delivery (the ongoing fee they'd otherwise pay).
 *
 * Returns undefined when the trial-box saving can't be determined, so the UI
 * can fall back to a placeholder rather than showing a misleadingly low number.
 */
export const calculateTotalSavings = (
  subscription?: TSubscriptionData,
  delivery?: TDeliveryData,
): number | undefined => {
  const trial = subscription?.trial;
  if (
    typeof trial?.totalOriginalPrice !== "number"
    || typeof trial?.totalDiscountedPrice !== "number"
  ) {
    return undefined;
  }

  // Trial-box totals already include the Nibbles Trio, so this covers the
  // fresh food discount and the nibbles half-price saving.
  const freshSavings = trial.totalOriginalPrice - trial.totalDiscountedPrice;

  const sprinkles = subscription?.sprinklesUpsell;
  const sprinklesSavings = (sprinkles?.originalPrice ?? 0) * (sprinkles?.quantity ?? 0);

  const deliverySavings = delivery?.trialDeliveryFee === 0 ? (delivery?.ongoingDeliveryFee ?? 0) : 0;

  return freshSavings + sprinklesSavings + deliverySavings;
};

/**
 * The "all-in" trial-box RRP shown struck-through next to what the customer pays
 * today. Unlike trial.totalOriginalPrice (fresh + nibbles only), this also adds
 * the free sprinkles and free delivery at their full price, so the struck-through
 * total reconciles with the rows: struck - paid === total savings.
 *
 * Returns undefined when the trial-box original can't be determined, so the UI
 * can fall back to a placeholder.
 */
export const calculateTrialBoxOriginalPrice = (
  subscription?: TSubscriptionData,
  delivery?: TDeliveryData,
): number | undefined => {
  const trial = subscription?.trial;
  if (typeof trial?.totalOriginalPrice !== "number") {
    return undefined;
  }

  const sprinkles = subscription?.sprinklesUpsell;
  const sprinklesOriginal = (sprinkles?.originalPrice ?? 0) * (sprinkles?.quantity ?? 0);

  const deliveryOriginal = delivery?.trialDeliveryFee === 0 ? (delivery?.ongoingDeliveryFee ?? 0) : 0;

  return trial.totalOriginalPrice + sprinklesOriginal + deliveryOriginal;
};

export const formatSavings = (savings: number | undefined, fallback = "--"): string => {
  if (savings === undefined || savings === null || Number.isNaN(savings)) { return fallback; }
  return `-£${savings.toFixed(2)}`;
};

/**
 * Formats a cat's selected recipes as a comma-separated, human-readable list,
 * e.g. "6 x Cluck, 3 x Shredded Cluck, 1 x Mooo".
 */
export const formatRecipeList = (
  selection: TRecipeSelection | undefined,
  translateRecipe: (pascalKey: string) => string,
): string => {
  if (!selection?.recipes?.length) { return ""; }
  return selection.recipes
    .map((recipe) => `${recipe.quantity} x ${translateRecipe(snakeToPascal(recipe.type))}`)
    .join(", ");
};
